import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SERVICES, PACKAGES, TIME_SLOTS } from "@/data/site";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { Loader2, Upload, X, Calendar as CalIcon, Clock, Camera, CreditCard, ArrowRight } from "lucide-react";

function formatDate(d) { return d.toISOString().split("T")[0]; }

function buildNext30Days() {
  const days = []; const today = new Date(); today.setHours(0,0,0,0);
  for (let i = 1; i <= 30; i++) { const d = new Date(today); d.setDate(today.getDate() + i); days.push(d); }
  return days;
}

const ALL_OPTIONS = [
  ...PACKAGES.map((p) => ({ slug: `${p.name.toLowerCase()}-package`, label: `${p.name} Package`, est: p.priceNum })),
  ...SERVICES.map((s) => ({ slug: s.slug, label: s.name, est: Number(String(s.rows[0][1]).replace(/[^0-9.]/g, "")) || 50 })),
];

export default function Book() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preSlug = params.get("service") || (params.get("package") ? `${params.get("package")}-package` : "");
  const preOption = ALL_OPTIONS.find((o) => o.slug === preSlug) || ALL_OPTIONS[0];

  const [step, setStep] = useState(1);
  const [serviceSlug, setServiceSlug] = useState(preOption.slug);
  const [estimated, setEstimated] = useState(preOption.est);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ address: "", postcode: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const days = useMemo(() => buildNext30Days(), []);
  const platformFee = useMemo(() => Math.max(estimated * 0.1, 5).toFixed(2), [estimated]);
  const selectedOption = ALL_OPTIONS.find((o) => o.slug === serviceSlug) || ALL_OPTIONS[0];

  useEffect(() => { setEstimated(selectedOption.est); /* eslint-disable-next-line */ }, [serviceSlug]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const handleFiles = async (files) => {
    if (!user || !user.id) { navigate(`/login?next=${encodeURIComponent('/book')}`); return; }
    setUploading(true); setError("");
    try {
      const newPhotos = [];
      for (const f of Array.from(files).slice(0, 5)) {
        const r = await uploadToCloudinary(f, "bookings");
        newPhotos.push(r);
      }
      setPhotos((prev) => [...prev, ...newPhotos].slice(0, 5));
    } catch (e) {
      setError(formatErr(e?.message) || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!user || !user.id) { navigate(`/login?next=${encodeURIComponent('/book')}`); return; }
    setError(""); setLoading(true);
    try {
      const payload = {
        service: selectedOption.label,
        estimated_value_gbp: Number(estimated),
        postcode: form.postcode.toUpperCase().trim(),
        address: form.address,
        preferred_date: date,
        preferred_time: time,
        notes: form.notes,
        photo_urls: photos.map((p) => p.secure_url),
      };
      const r = await api.post("/bookings", payload);
      const checkout = await api.post("/payments/checkout", { booking_id: r.data.id, origin_url: window.location.origin });
      window.location.href = checkout.data.url;
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail) || e.message);
      setLoading(false);
    }
  };

  const canStep2 = !!serviceSlug && Number(estimated) > 0;
  const canStep3 = !!date && !!time;
  const canStep4 = !!form.address && !!form.postcode;

  return (
    <div data-testid="book-page" className="pt-32">
      <section className="max-w-5xl mx-auto px-5 md:px-8 pb-10">
        <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Book a Pro</div>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl text-[#F0F4F1]">
          Tell us about <span className="text-[#8DE038]">the job.</span>
        </h1>
      </section>

      <section className="section-pad bg-[#0A0F0B]">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="border border-[#1F3325] bg-[#0F1712] p-6 md:p-10">
            <div className="flex items-center gap-3 mb-8 flex-wrap">
              {[{ n: 1, l: "Service" }, { n: 2, l: "Date & time" }, { n: 3, l: "Photos & address" }, { n: 4, l: "Pay fee" }].map((s, i) => (
                <div key={s.n} className="flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center font-display uppercase text-sm border ${step >= s.n ? "bg-[#8DE038] border-[#8DE038] text-[#050806]" : "border-[#1F3325] text-[#9CAEA3]"}`}>{s.n}</div>
                  <span className={`font-display uppercase tracking-widest text-xs hidden sm:inline ${step >= s.n ? "text-[#F0F4F1]" : "text-[#9CAEA3]"}`}>{s.l}</span>
                  {i < 3 && <div className="w-6 h-px bg-[#1F3325]"/>}
                </div>
              ))}
            </div>

            {step === 1 && (
              <div data-testid="step-service" className="space-y-5">
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Service</label>
                  <select data-testid="book-service-select" className="gl-input mt-2" value={serviceSlug} onChange={(e)=>setServiceSlug(e.target.value)}>
                    {ALL_OPTIONS.map((o) => <option key={o.slug} value={o.slug}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Estimated job value (£)</label>
                  <input data-testid="book-estimated" type="number" min="1" step="1" className="gl-input mt-2" value={estimated} onChange={(e)=>setEstimated(Number(e.target.value))}/>
                  <p className="text-xs text-[#6B7C71] mt-2">Rough estimate from the price list. Pro confirms on the day. <span className="text-[#8DE038]">Platform fee: 10% (min £5) → £{platformFee}</span></p>
                </div>
                <button data-testid="step-next-1" disabled={!canStep2} onClick={()=>setStep(2)} className="btn-primary disabled:opacity-50">Next <ArrowRight size={14}/></button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-datetime" className="space-y-6">
                <div>
                  <div className="flex items-center gap-2 mb-3"><CalIcon size={16} className="text-[#8DE038]"/><span className="font-display uppercase tracking-widest text-sm text-[#F0F4F1]">Pick a date</span></div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2">
                    {days.map((d) => {
                      const iso = formatDate(d);
                      const isSunday = d.getDay() === 0;
                      const active = date === iso;
                      return (
                        <button key={iso} data-testid={`date-${iso}`} disabled={isSunday} onClick={() => setDate(iso)}
                          className={`p-3 border text-center transition-colors ${active ? "border-[#8DE038] bg-[#8DE038]/10" : "border-[#1F3325] hover:border-[#8DE038]"} ${isSunday ? "opacity-30 cursor-not-allowed" : ""}`}>
                          <div className="font-display uppercase text-[10px] tracking-widest text-[#9CAEA3]">{d.toLocaleDateString("en-GB", { weekday: "short" })}</div>
                          <div className="font-display text-2xl text-[#F0F4F1] mt-1">{d.getDate()}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#9CAEA3]">{d.toLocaleDateString("en-GB", { month: "short" })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {date && (
                  <div>
                    <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-[#8DE038]"/><span className="font-display uppercase tracking-widest text-sm text-[#F0F4F1]">Pick a time on {date}</span></div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {TIME_SLOTS.map((t) => (
                        <button key={t} data-testid={`time-${t}`} onClick={() => setTime(t)}
                          className={`p-3 border font-display tracking-widest transition-colors ${time === t ? "border-[#8DE038] bg-[#8DE038]/10 text-[#8DE038]" : "border-[#1F3325] text-[#F0F4F1] hover:border-[#8DE038]"}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={()=>setStep(1)} className="btn-secondary text-xs">← Back</button>
                  <button data-testid="step-next-2" disabled={!canStep3} onClick={()=>setStep(3)} className="btn-primary disabled:opacity-50">Next <ArrowRight size={14}/></button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-details" className="space-y-5">
                <div>
                  <div className="flex items-center gap-2 mb-3"><Camera size={16} className="text-[#8DE038]"/><span className="font-display uppercase tracking-widest text-sm text-[#F0F4F1]">Photos (up to 5)</span></div>
                  <div className="border-2 border-dashed border-[#1F3325] hover:border-[#8DE038] p-6 text-center cursor-pointer transition-colors" onClick={()=>fileRef.current?.click()} data-testid="upload-zone">
                    {uploading ? <div className="flex items-center justify-center gap-2 text-[#9CAEA3]"><Loader2 className="animate-spin" size={18}/> Uploading…</div> :
                      <div className="text-[#9CAEA3] text-sm"><Upload className="inline mr-1" size={14}/> Click to upload photos of the job</div>}
                    <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e)=>handleFiles(e.target.files)} data-testid="photo-input"/>
                  </div>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                      {photos.map((p, i) => (
                        <div key={p.public_id} className="relative aspect-square overflow-hidden border border-[#1F3325]" data-testid={`photo-${i}`}>
                          <img src={p.secure_url} alt="" className="w-full h-full object-cover"/>
                          <button onClick={()=>removePhoto(i)} className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-red-600"><X size={12}/></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Address</label>
                  <input data-testid="book-address" required className="gl-input mt-2" value={form.address} onChange={(e)=>update("address", e.target.value)} placeholder="12 Elm Road, Leeds"/>
                </div>
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Postcode</label>
                  <input data-testid="book-postcode" required className="gl-input mt-2" value={form.postcode} onChange={(e)=>update("postcode", e.target.value)} placeholder="LS1 1AA"/>
                </div>
                <div>
                  <label className="font-display uppercase tracking-widest text-xs text-[#9CAEA3]">Notes for the pro</label>
                  <textarea data-testid="book-notes" rows={3} className="gl-input mt-2 resize-none" value={form.notes} onChange={(e)=>update("notes", e.target.value)} placeholder="Access, gate code, condition…"/>
                </div>
                <div className="flex gap-3">
                  <button onClick={()=>setStep(2)} className="btn-secondary text-xs">← Back</button>
                  <button data-testid="step-next-3" disabled={!canStep4} onClick={()=>setStep(4)} className="btn-primary disabled:opacity-50">Review &amp; Pay <ArrowRight size={14}/></button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-pay" className="space-y-5">
                <h2 className="font-display uppercase tracking-tighter text-3xl text-[#F0F4F1] heading-accent">Confirm &amp; pay platform fee</h2>
                <div className="bg-[#050806] border border-[#1F3325] p-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#9CAEA3]">Service</span><span className="text-[#F0F4F1]">{selectedOption.label}</span></div>
                  <div className="flex justify-between"><span className="text-[#9CAEA3]">Date / time</span><span className="text-[#F0F4F1]">{date} · {time}</span></div>
                  <div className="flex justify-between"><span className="text-[#9CAEA3]">Address</span><span className="text-[#F0F4F1]">{form.address}, {form.postcode}</span></div>
                  <div className="flex justify-between"><span className="text-[#9CAEA3]">Photos</span><span className="text-[#F0F4F1]">{photos.length}</span></div>
                  <div className="flex justify-between"><span className="text-[#9CAEA3]">Estimated job value</span><span className="text-[#F0F4F1]">£{estimated}</span></div>
                  <div className="border-t border-[#1F3325] mt-3 pt-3 flex justify-between text-lg">
                    <span className="font-display uppercase tracking-widest text-[#8DE038]">Platform fee (10% min £5)</span>
                    <span className="font-display text-[#8DE038]">£{platformFee}</span>
                  </div>
                </div>
                <p className="text-xs text-[#6B7C71]">Platform fee paid now securely via SumUp. Pay the contractor directly (cash or bank transfer) for the actual work on the day.</p>
                {error && <div data-testid="book-error" className="text-red-400 text-sm border border-red-500/40 bg-red-900/20 p-3">{error}</div>}
                <div className="flex gap-3">
                  <button onClick={()=>setStep(3)} className="btn-secondary text-xs">← Back</button>
                  <button data-testid="book-submit" disabled={loading} onClick={submit} className="btn-primary disabled:opacity-60">
                    {loading ? <Loader2 className="animate-spin" size={18}/> : <><CreditCard size={16}/> Pay £{platformFee} &amp; Submit</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
